import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePeriodBuffer } from '../context/PeriodBufferContext';
import { Form, Dropdown, Alert, Spinner, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import InventoryProductTile from '../components/inventory/InventoryProductTile';

const Inventory = ({
  setActiveView,                 // may be undefined
  setSelectedProduct,            // may be undefined
  selectedCategory: selectedCategoryProp,   // may be undefined
  setSelectedCategory: setSelectedCategoryProp, // may be undefined
}) => {
  // ---- category with safe parent fallback ----
  const [localCategory, setLocalCategory] = useState(selectedCategoryProp !== undefined ? selectedCategoryProp : 'ALL_DUE');
  useEffect(() => {
    if (typeof selectedCategoryProp !== 'undefined') {
      setLocalCategory(selectedCategoryProp);
    }
  }, [selectedCategoryProp]);

  const category = typeof selectedCategoryProp !== 'undefined' ? selectedCategoryProp : localCategory;
  const setCategory =
    typeof setSelectedCategoryProp === 'function' ? setSelectedCategoryProp : setLocalCategory;

  // ---- other state ----
  const [categories, setCategories] = useState([]);
  const [inventoryPeriods, setInventoryPeriods] = useState([]);
  const [selectedInventoryPeriod, setSelectedInventoryPeriod] = useState(''); // '' = All Frequencies
  const [products, setProducts] = useState([]); // items to render (id, name, imageURL)
  const [, setInvItems] = useState([]); // raw inventory items
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noInventoryItems, setNoInventoryItems] = useState(false);
  // Auto-dismissing toast shown after a successful flip-tile update.
  const [updateToast, setUpdateToast] = useState('');
  const toastTimerRef = useRef(null);

  const { isAuthenticated, verifySession } = useAuth();
  const { periodBuffers, initialized, fetchPeriodBuffers } = usePeriodBuffer();
  const navigate = useNavigate();
  const location = useLocation();
  const lastLocationKey = useRef(null);

  // Handle category from navigation state (only when location key changes, indicating new navigation)
  useEffect(() => {
    const presetCategory = location.state?.category;
    const currentKey = location.key;

    if (presetCategory !== undefined && currentKey !== lastLocationKey.current) {
      setCategory(presetCategory);
      setSelectedInventoryPeriod('');
      lastLocationKey.current = currentKey;
      // Clear the navigation state to prevent it from interfering with dropdown selection
      navigate('/dashboard/inventory', { replace: true, state: {} });
    }
  }, [location.key, location.state?.category, navigate, setCategory]);

  const shouldShowNoInventoryMessage = useCallback((error) =>
    error.response?.data?.errorType === 'NotFoundError' ||
    (error.response?.status === 404 && error.response?.data?.message?.includes('not found')) ||
    error.response?.data?.code === 'INVENTORY_NOT_FOUND' ||
    error.response?.status === 500 ||
    error.message?.includes('Failed to load data') ||
    error.response?.data?.message?.includes('Failed to load data'), []);

  // Try to enrich products by a bulk lookup; fail soft if endpoint not present
  const fetchProductsByIds = useCallback(async (ids, category) => {
  const normalized = Array.isArray(ids) ? ids : [ids].filter(Boolean);
  if (!normalized.length) return [];

  try 
  {
    const payload = { id: normalized };
    if (category) payload.category = category;

    const res = await axios.post('/products/by-ids', payload, { withCredentials: true });

    // Normalise response: controller returns a single Product object for 1 id,
    // or { success, data: [...] } for multiple.
    if (Array.isArray(res.data?.data)) return res.data.data;       // multi
    if (Array.isArray(res.data)) return res.data;                   // (if you returned an array)
    if (res.data && typeof res.data === 'object') return [res.data]; // single -> array
    return [];
  } 
  catch (e) 
  {
    console.warn('Bulk /products/by-ids failed, falling back to inventory-only display.', e?.response?.data || e.message);
    return [];
  }
}, []);

  const isItemDueForStockTake = useCallback((inventoryItem, periods = inventoryPeriods, buffers = periodBuffers) => {
    if (!inventoryItem.lastStockTake || !inventoryItem.inventoryPeriod) {
      return false;
    }

    const lastStockTakeDate = new Date(inventoryItem.lastStockTake);
    const currentDate = new Date();

    // Find the period ID based on inventoryPeriod (days)
    const period = periods.find(p => p.inventoryDays === inventoryItem.inventoryPeriod);

    // Get buffer hours for this period (default to 0 if not found)
    const bufferHours = period && buffers.has(period.id) ? buffers.get(period.id) : 0;

    // Calculate period in milliseconds and subtract buffer
    const inventoryPeriodInMs = inventoryItem.inventoryPeriod * 24 * 60 * 60 * 1000;
    const bufferInMs = bufferHours * 60 * 60 * 1000;
    const adjustedPeriodInMs = inventoryPeriodInMs - bufferInMs;

    const nextStockTakeDate = new Date(lastStockTakeDate.getTime() + adjustedPeriodInMs);

    return nextStockTakeDate <= currentDate;
  }, [inventoryPeriods, periodBuffers]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setNoInventoryItems(false);
      await verifySession();

      // Fetch period buffers if not initialized and get the bufferMap to use locally
      let bufferMap = periodBuffers;
      if (!initialized) {
        try {
          bufferMap = await fetchPeriodBuffers();
        } catch (err) {
          // Ignore errors - buffers are optional
          console.warn('Period buffers could not be loaded, continuing without them');
          bufferMap = new Map();
        }
      }

      // Categories
      const categoriesRes = await axios.get('/categories/getAllCategories', { withCredentials: true });
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.data || []);

      // Inventory periods
      const periodsRes = await axios.get('/inventory-periods', { withCredentials: true });
      const periods = periodsRes.data?.data || [];
      setInventoryPeriods(periods);

      // In-stock inventory items
      let allInv = [];
      try {
        const invRes = await axios.get('/product-inventory/stock', { withCredentials: true });
        allInv = invRes.data?.data || [];
        setInvItems(allInv);
      } catch (err) {
        if (shouldShowNoInventoryMessage(err)) {
          setNoInventoryItems(true);
          setInvItems([]);
        } else {
          throw err;
        }
      }

      // ---- Build render list ----
      // Handle "All Due" category - show items due for stock take
      if (category === 'ALL_DUE') {
        let dueItems = allInv.filter(item => isItemDueForStockTake(item, periods, bufferMap));

        // Apply frequency filter if selected
        if (selectedInventoryPeriod) {
          const period = periods.find(p => p.id === selectedInventoryPeriod);
          if (period) {
            dueItems = dueItems.filter(item => item.inventoryPeriod === period.inventoryDays);
          }
        }

        if (!dueItems.length) {
          setProducts([]);
          setNoInventoryItems(true);
          return;
        }

        // Enrich by product details if endpoint exists
        const uniqueIds = [...new Set(dueItems.map((i) => i.productUuid).filter(Boolean))];
        const prodDetails = await fetchProductsByIds(uniqueIds);
        const prodMap = new Map(prodDetails.map((p) => [p.id, p]));

        // Build render list (dedup by productUuid)
        const dedup = new Map();
        dueItems.forEach((inv) => {
          const p = prodMap.get(inv.productUuid) || {};
          const item = {
            id: inv.productUuid,
            name: p.name || inv.productName || 'Unnamed Product',
            imageURL: p.imageURL || inv.imageURL || '/placeholder-product.png',
          };
          if (!dedup.has(item.id)) dedup.set(item.id, item);
        });

        setProducts([...dedup.values()]);
        return;
      }

      // If a specific category is selected, preserve existing behaviour:
      // fetch products for that category, intersect with inventory, and apply frequency filter.
      if (category) {
        let allProducts = [];
        const prodRes = await axios.post(
          '/products/by-category',
          { category },
          { withCredentials: true }
        );
        allProducts = Array.isArray(prodRes.data) ? prodRes.data : prodRes.data?.data || [];

        // apply inventory intersection
        const inStockProducts = allProducts.filter((p) =>
          allInv.some((i) => i.productUuid === p.id)
        );

        // optional frequency filter
        let finalProducts = inStockProducts;
        if (selectedInventoryPeriod) {
          const period = periods.find((per) => per.id === selectedInventoryPeriod);
          if (period) {
            finalProducts = inStockProducts.filter((p) => {
              const inv = allInv.find((i) => i.productUuid === p.id);
              return inv && inv.inventoryPeriod === period.inventoryDays;
            });
          }
        }

        // normalize to render shape
        setProducts(
          finalProducts.map((p) => ({
            id: p.id,
            name: p.name,
            imageURL: p.imageURL || '/placeholder-product.png',
          }))
        );
        return;
      }

      // If NO category is selected (i.e., "All Categories"), show everything from inventory,
      // optionally filtered by frequency, and enrich with product details if possible.
      let invForRender = allInv;

      if (selectedInventoryPeriod) {
        const period = periods.find((per) => per.id === selectedInventoryPeriod);
        if (period) {
          invForRender = invForRender.filter(
            (inv) => inv.inventoryPeriod === period.inventoryDays
          );
        }
      }

      if (!invForRender.length) {
        setProducts([]);
        setNoInventoryItems(true);
        return;
      }

      // Enrich by product details if endpoint exists
      const uniqueIds = [...new Set(invForRender.map((i) => i.productUuid).filter(Boolean))];
      const prodDetails = await fetchProductsByIds(uniqueIds);
      const prodMap = new Map(prodDetails.map((p) => [p.id, p]));

      // Build render list (dedup by productUuid)
      const dedup = new Map();
      invForRender.forEach((inv) => {
        const p = prodMap.get(inv.productUuid) || {};
        const item = {
          id: inv.productUuid,
          name: p.name || inv.productName || 'Unnamed Product',
          imageURL: p.imageURL || inv.imageURL || '/placeholder-product.png',
        };
        if (!dedup.has(item.id)) dedup.set(item.id, item);
      });

      setProducts([...dedup.values()]);
    } catch (err) {
      console.error(err);
      if (shouldShowNoInventoryMessage(err)) {
        setNoInventoryItems(true);
        setProducts([]);
      } else {
        setError(err.response?.data?.message || 'Failed to load data');
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchData();
    if (typeof setActiveView === 'function') setActiveView('inventory');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, category, selectedInventoryPeriod]); // refetch when these change

  const showFlashToast = useCallback((productId, productName) => {
    setUpdateToast(productName ? `${productName} updated` : 'Stock updated');
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setUpdateToast(''), 2000);
    // After a successful stock-take, drop the item from the "All Due" list —
    // it's no longer due. Other category views are product-scoped, not
    // due-scoped, so the item should remain visible there.
    if (productId && category === 'ALL_DUE') {
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    }
  }, [category]);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <div className="content-container">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {updateToast && (
        <div className="inventory-flash-toast" role="status">
          {updateToast}
        </div>
      )}

      <div className="d-flex align-items-center mb-4">
        {/* Categories */}
        <div className="d-flex align-items-center me-4">
          <Form.Label className="me-2 mb-0">Categories:</Form.Label>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">
              {category === ''
                ? 'All Categories'
                : category === 'ALL_DUE'
                ? 'All Due'
                : (categories.find((c) => c.id === category)?.name || 'Select Category')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setCategory('')} active={category === ''}>
                All Categories
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setCategory('ALL_DUE')} active={category === 'ALL_DUE'}>
                All Due
              </Dropdown.Item>
              {categories.map((c) => (
                <Dropdown.Item
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  active={category === c.id}
                >
                  {c.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Stock Take Frequency */}
        <div className="d-flex align-items-center">
          <Form.Label className="me-2 mb-0">Stock Take Frequency:</Form.Label>
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary">
              {selectedInventoryPeriod === ''
                ? 'All Frequencies'
                : (
                    inventoryPeriods.find((p) => p.id === selectedInventoryPeriod)?.inventoryPeriodDesc ||
                    inventoryPeriods.find((p) => p.id === selectedInventoryPeriod)?.inventoryPeriodName ||
                    'Select Frequency'
                  )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => setSelectedInventoryPeriod('')}
                active={selectedInventoryPeriod === ''}
              >
                All Frequencies
              </Dropdown.Item>
              {inventoryPeriods.map((p) => (
                <Dropdown.Item
                  key={p.id}
                  onClick={() => setSelectedInventoryPeriod(p.id)}
                  active={selectedInventoryPeriod === p.id}
                >
                  {p.inventoryPeriodDesc || p.inventoryPeriodName}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : noInventoryItems ? (
        <div className="text-center py-5">
          <Alert variant="info">
            No Inventory Items to Display. Ensure Product Items are flagged for Inventory management
          </Alert>
        </div>
      ) : (
        <div className="product-grid-container">
          {products.length > 0 ? (
            <Row className="g-4">
              {products.map((product) => (
                <Col key={product.id} xs={6} sm={4} md={3} lg={2} xl={2} xxl={2}>
                  <InventoryProductTile
                    product={product}
                    category={category}
                    onUpdateSuccess={showFlashToast}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              No in-stock products match your filters
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Inventory;